import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Typography } from "@mui/material";
import { Box, useTheme } from "@mui/system";
import HighchartsReact from "highcharts-react-official";
import HighchartsMore from "highcharts/highcharts-more";
import Highcharts, { ColorType, YAxisOptions } from "highcharts/highstock";
import HighchartsBoost from "highcharts/modules/boost";
import { DateTime } from "luxon";

import { createChartTooltip } from "@utilifeed/core/utils/chartTooltip";

import type { DateRange, GraphSettings, Resolution } from "../shared/types";
import { RESOLUTION_OPTIONS } from "./chartConstants";

// Initialize Highcharts modules
if (typeof Highcharts === "object") {
  HighchartsBoost(Highcharts);
  HighchartsMore(Highcharts);
}

export type TimeSeriesDataPoint = {
  datetime: string;
  value: number;
  flags?: string[];
};

export type TimeSeriesRangePoint = {
  datetime: string;
  low: number;
  high: number;
};

export type TimeSeriesData = {
  uid: string;
  name: string;
  data: TimeSeriesDataPoint[];
  unit: string;
  color?: ColorType;
  yAxisIndex?: number;
  visible?: boolean;
  dashStyle?: string;
  // Optional overrides for special-purpose series (e.g. an arearange band
  // rendered alongside two regular line series).
  type?: "spline" | "arearange";
  rangeData?: TimeSeriesRangePoint[];
  fillOpacity?: number;
  lineWidth?: number;
  zIndex?: number;
  enableMouseTracking?: boolean;
  showInLegend?: boolean;
  connectNulls?: boolean;
};

export type YAxisConfig = YAxisOptions;

export type BaseTimeSeriesChartProps = {
  series: TimeSeriesData[];
  yAxisConfigs: YAxisConfig[];

  chartHeight?: number;
  chartTitle?: string;

  dateRange?: DateRange;
  resolution?: Resolution;
  graphSettings?: GraphSettings;

  dataGroupingUnits?: Array<[string, number[]]>;
  dataGroupingPixelWidth?: number;

  onSeriesToggle?: (seriesUid: string) => void;
  allowSeriesToggle?: boolean;

  isLoading?: boolean;
  yAxisLayout?: "stacked" | "side-by-side"; // New prop for y-axis layout

  // Whether to draw lines across null data points.
  connectNulls?: boolean;
  // Gap threshold in milliseconds.
  gapSize?: number;

  // Custom legend rendered alongside the chart for sensor grids.
  legendEnabled?: boolean;
};

const CHART_DEFAULTS = {
  LEGEND_HEIGHT: 42,
  BOTTOM_MARGIN: 88,
  DEFAULT_HEIGHT: 550,
};

// Highcharts-compatible styles mapped to semantic typography variants
const CHART_TYPOGRAPHY = {
  labelLarge: { fontSize: "14px", fontWeight: "500" },
  labelMedium: { fontSize: "12px", fontWeight: "500" },
  bodySmall: { fontSize: "12px", fontWeight: "300" },
};

/**
 * BaseTimeSeriesChart - My TRUE reusable time series chart component
 *
 * This component provides the core charting functionality to be wrapped by container components
 */
export const BaseTimeSeriesChart: React.FC<BaseTimeSeriesChartProps> = ({
  series,
  yAxisConfigs,
  chartHeight = CHART_DEFAULTS.DEFAULT_HEIGHT,
  chartTitle,
  dateRange = { startDate: null, endDate: null },
  resolution = "hourly",
  graphSettings = { average: false, min: false, max: false, grid: false },
  dataGroupingUnits,
  dataGroupingPixelWidth,
  onSeriesToggle,
  allowSeriesToggle = true,
  isLoading = false,
  yAxisLayout = "stacked", // Default to stacked for backward compatibility
  connectNulls = true, // Default to true for backward compatibility
  gapSize,
  legendEnabled = true,
}) => {
  const theme = useTheme();
  const { t, i18n } = useTranslation("_common");
  const chartRef = useRef<HighchartsReact.RefObject>(null);

  useEffect(() => {
    Highcharts.setOptions({
      lang: {
        months: Array.from({ length: 12 }, (_, i) => t(`text_month_${i + 1}`)),
        shortMonths: Array.from({ length: 12 }, (_, i) => t(`text_month_short_${i + 1}`)),
      },
    });
  }, [i18n.language, t]);

  // Track series visibility
  const [seriesVisibility, setSeriesVisibility] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    series.forEach((s) => {
      initial[s.uid] = s.visible !== false;
    });
    return initial;
  });

  // Sync visibility from parent series prop
  useEffect(() => {
    setSeriesVisibility(() => {
      const updated: Record<string, boolean> = {};
      series.forEach((s) => {
        updated[s.uid] = s.visible !== false;
      });
      return updated;
    });
  }, [series]);

  const handleSeriesToggle = useCallback(
    (seriesUid: string) => {
      // Prevent hiding all series
      const visibleCount = Object.values(seriesVisibility).filter((v) => v).length;
      if (visibleCount === 1 && seriesVisibility[seriesUid]) {
        return;
      }

      setSeriesVisibility((prev) => ({
        ...prev,
        [seriesUid]: !prev[seriesUid],
      }));

      onSeriesToggle?.(seriesUid);
    },
    [seriesVisibility, onSeriesToggle]
  );

  // Chart options
  const chartOptions = useMemo((): Highcharts.Options => {
    // Filter data by date range
    const filterDataByDateRange = (data: TimeSeriesDataPoint[]) => {
      if (!dateRange.startDate && !dateRange.endDate) return data;

      return data.filter((point) => {
        const pointDate = DateTime.fromISO(point.datetime);
        if (dateRange.startDate && pointDate < dateRange.startDate) return false;
        if (dateRange.endDate && pointDate > dateRange.endDate) return false;
        return true;
      });
    };

    // Build y-axis — only count axes that have visible series WITH data in range
    const visibleYAxes = new Set<number>();
    series.forEach((s) => {
      if (seriesVisibility[s.uid]) {
        const hasPoints =
          s.type === "arearange"
            ? (s.rangeData?.length ?? 0) > 0
            : filterDataByDateRange(s.data).length > 0;
        if (hasPoints) {
          visibleYAxes.add(s.yAxisIndex || 0);
        }
      }
    });

    const legendReservedHeight = legendEnabled ? CHART_DEFAULTS.LEGEND_HEIGHT : 0;
    const availableHeight = chartHeight - legendReservedHeight - CHART_DEFAULTS.BOTTOM_MARGIN;

    const yAxis: Highcharts.YAxisOptions[] = [];

    if (yAxisLayout === "side-by-side") {
      // Side-by-side layout - axes share the full height but are positioned on the left and right
      yAxisConfigs.forEach((config) => {
        const { title, labels, ...rest } = config;
        yAxis.push({
          labels: {
            style: CHART_TYPOGRAPHY.bodySmall,
            formatter: function () {
              const val = (this as any).value as number;
              return val.toFixed(1);
            },
            ...labels,
          },
          title: { style: CHART_TYPOGRAPHY.labelMedium, ...title },
          gridLineWidth: graphSettings.grid ? 1 : 0,
          // TODO: Probably make this a setting
          opposite: false,
          showEmpty: false,
          lineColor: theme.palette?.divider,
          ...rest,
        });
      });
    } else {
      // Stacked layout (original behavior)
      const visibleAxisCount = Math.max(visibleYAxes.size, 1);
      const axisHeight = availableHeight / visibleAxisCount;
      let currentOffset = legendReservedHeight;

      yAxisConfigs.forEach((config, index) => {
        const hasData = visibleYAxes.has(index);
        const { title, labels, ...rest } = config;
        yAxis.push({
          labels: {
            style: CHART_TYPOGRAPHY.bodySmall,
            formatter: function () {
              const val = (this as any).value as number;
              return val.toFixed(1);
            },
            ...labels,
          },
          title: { style: CHART_TYPOGRAPHY.labelMedium, ...title },
          height: hasData ? axisHeight : 0,
          top: currentOffset,
          offset: 0,
          showEmpty: false,
          gridLineWidth: graphSettings.grid ? 1 : 0,
          gridLineColor: theme.palette?.divider,
          opposite: false,
          lineWidth: 1,
          lineColor: theme.palette?.divider,
          ...rest,
        });

        if (hasData) {
          currentOffset += axisHeight;
        }
      });
    }

    // Build series
    const chartSeries: Highcharts.SeriesOptionsType[] = series.map((s) => {
      const yAxisIndex = s.yAxisIndex || 0;
      const showInLegend = s.showInLegend ?? true;

      if (s.type === "arearange") {
        const rangePoints = (s.rangeData ?? []).map((p) => [
          DateTime.fromISO(p.datetime).toMillis(),
          p.low,
          p.high,
        ]);
        return {
          name: s.name,
          id: s.uid,
          type: "arearange" as const,
          data: rangePoints,
          tooltip: { valueSuffix: s.unit },
          color: s.color,
          yAxis: yAxisIndex,
          visible: seriesVisibility[s.uid],
          showInLegend,
          fillOpacity: s.fillOpacity ?? 0.18,
          lineWidth: s.lineWidth ?? 0,
          zIndex: s.zIndex ?? 0,
          enableMouseTracking: s.enableMouseTracking ?? false,
          marker: { enabled: false },
          dataGrouping: { approximation: "range" },
        };
      }

      const filteredData = filterDataByDateRange(s.data);
      return {
        name: s.name,
        id: s.uid,
        type: "spline" as const,
        data: filteredData.map((point) => [
          DateTime.fromISO(point.datetime).toMillis(),
          point.value,
        ]),
        tooltip: { valueSuffix: s.unit },
        color: s.color,
        yAxis: yAxisIndex,
        visible: seriesVisibility[s.uid],
        showInLegend,
        lineWidth: s.lineWidth ?? 2,
        dashStyle: s.dashStyle as any,
        zIndex: s.zIndex,
        marker: {
          enabled: true,
          enabledThreshold: 5,
          radius: 3,
          symbol: "circle",
        },
        states: {
          hover: { lineWidth: 3 },
        },
        ...(gapSize !== undefined && {
          gapSize,
          gapUnit: "value" as const,
        }),
        ...(s.connectNulls !== undefined && { connectNulls: s.connectNulls }),
      };
    });

    // Set data grouping based on resolution
    const dataGrouping = {
      enabled: resolution !== "as_measured",
      units: (dataGroupingUnits || RESOLUTION_OPTIONS[resolution].units) as unknown as [
        string,
        number[],
      ][],
      approximation: "average" as const,
      groupPixelWidth: dataGroupingPixelWidth ?? 100,
      smoothed: false,
      forced: resolution !== "as_measured",
    };

    // Calculate minRange based on resolution
    const calculateMinRange = (): number | undefined => {
      if (resolution === "hourly") {
        return 60 * 60 * 1000; // 1 hour
      }
      if (resolution === "daily") {
        return 24 * 60 * 60 * 1000; // 1 day
      }
      if (resolution === "as_measured") {
        // Detect the minimum interval between data points
        let minInterval: number | undefined;

        series.forEach((s) => {
          const sortedData = [...s.data]
            .map((point) => DateTime.fromISO(point.datetime).toMillis())
            .filter((ts) => !isNaN(ts))
            .sort((a, b) => a - b);

          if (sortedData.length >= 2) {
            for (let i = 1; i < sortedData.length; i++) {
              const interval = sortedData[i] - sortedData[i - 1];
              if (interval > 0 && (minInterval === undefined || interval < minInterval)) {
                minInterval = interval;
              }
            }
          }
        });

        // Return the detected interval, default to 15 minutes if no data
        return minInterval ?? 15 * 60 * 1000;
      }
      return undefined;
    };

    const dividerColor = (theme.palette?.divider as string | undefined) ?? "#e0e0e0";

    // Draws thin horizontal lines between adjacent stacked y-axis panes
    const drawAxisSeparators = function (this: Highcharts.Chart) {
      const chart = this as Highcharts.Chart & { __axisSeparators?: Highcharts.SVGElement[] };
      chart.__axisSeparators?.forEach((el) => el.destroy());
      chart.__axisSeparators = [];

      if (yAxisLayout !== "stacked") return;

      const visibleAxes = chart.yAxis
        .filter((a) => (a.options.height as number | undefined) !== 0 && a.height > 0)
        .slice()
        .sort((a, b) => a.top - b.top);

      for (let i = 1; i < visibleAxes.length; i++) {
        const y = visibleAxes[i].top;
        const x1 = chart.plotLeft;
        const x2 = chart.plotLeft + chart.plotWidth;
        const line = chart.renderer
          .path(["M", x1, y, "L", x2, y])
          .attr({ stroke: dividerColor, "stroke-width": 1, zIndex: 4 })
          .add();
        chart.__axisSeparators.push(line);
      }
    };

    return {
      chart: {
        height: chartHeight,
        type: "spline",
        animation: false,
        spacing: [10, 30, 15, 10],
        // @ts-expect-error idk why this doesnt work
        zoomType: "x" as const,
        zooming: {
          mouseWheel: { enabled: false },
        },
        events: {
          render: drawAxisSeparators,
        },
      },
      title: chartTitle ? { text: chartTitle } : undefined,
      xAxis: {
        type: "datetime",
        ordinal: false,
        labels: { style: CHART_TYPOGRAPHY.bodySmall },
        // Only apply overscroll when no explicit date range bounds are set
        overscroll: dateRange?.startDate && dateRange?.endDate ? 0 : 3600 * 1000,
        min: dateRange?.startDate ? dateRange.startDate.toMillis() : undefined,
        max: dateRange?.endDate ? dateRange.endDate.toMillis() : undefined,
        minRange: calculateMinRange(),
        gridLineWidth: graphSettings.grid ? 1 : 0,
        lineColor: theme.palette?.divider,
        tickColor: theme.palette?.divider,
        lineWidth: 1,
      },
      yAxis,
      legend: {
        enabled: legendEnabled,
        align: "left",
        verticalAlign: "top",
        layout: "horizontal",
        symbolWidth: 16,
        symbolHeight: 12,
        useHTML: false,
        itemStyle: CHART_TYPOGRAPHY.labelLarge,
      },
      plotOptions: {
        series: {
          dataGrouping,
          showInLegend: true,
          connectNulls,
          events: allowSeriesToggle
            ? {
                legendItemClick: function (e) {
                  const seriesId = (this as any).options.id as string;
                  handleSeriesToggle(seriesId);
                  return false;
                },
              }
            : undefined,
        },
      },
      series: chartSeries,
      tooltip: createChartTooltip({ resolution }),
      rangeSelector: { enabled: false },
      navigator: { enabled: false },
      scrollbar: { enabled: true },
      credits: { enabled: false },
      boost: {
        useGPUTranslations: true,
        usePreallocated: true,
      },
    };
  }, [
    series,
    yAxisConfigs,
    seriesVisibility,
    chartHeight,
    dateRange?.startDate,
    dateRange?.endDate,
    resolution,
    graphSettings?.average,
    graphSettings?.min,
    graphSettings?.max,
    graphSettings?.grid,
    dataGroupingUnits,
    dataGroupingPixelWidth,
    theme,
    allowSeriesToggle,
    chartTitle,
    yAxisLayout,
    handleSeriesToggle,
    connectNulls,
    gapSize,
    legendEnabled,
  ]);

  if (isLoading) {
    return (
      <Box
        sx={{
          width: "100%",
          height: `${chartHeight}px`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography variant="bodyMedium" color="text.secondary">
          Loading...
        </Typography>
      </Box>
    );
  }

  const hasData = series.some((s) => s.data && s.data.length > 0);
  if (!hasData) {
    return (
      <Box
        sx={{
          height: `${chartHeight}px`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography variant="bodyMedium" color="text.secondary">
          No data available for the selected period
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      <HighchartsReact
        highcharts={Highcharts}
        constructorType="stockChart"
        options={chartOptions}
        ref={chartRef}
        containerProps={{ style: { width: "100%", height: "100%" } }}
      />
    </Box>
  );
};
